import { EnvironmentOutlined, EyeOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, Card } from 'antd';
import React, { useImperativeHandle, useRef, useState } from 'react';
import GoogleMapLocationPicker, {
  type GoogleMapLocationPickerHandle,
  type GoogleMapLocationPickerProps,
} from './index';

export interface LazyGoogleMapLocationPickerProps
  extends GoogleMapLocationPickerProps {
  /** 懒加载按钮文本 */
  buttonText?: string;
  /** 是否显示地址预览（在按钮中） */
  showAddressPreview?: boolean;
}

/**
 * 懒加载的 Google 地图地点选择器组件
 *
 * 默认只显示一个按钮，点击后才加载真正的地图组件，节省资源
 *
 * @example
 * ```tsx
 * <LazyGoogleMapLocationPicker
 *   onPlaceChange={(place) => console.log('地点变化:', place)}
 *   initialStructuredAddress={structuredAddress}
 * />
 * ```
 */
const placeFirstLabelId = '组件.地址.请先选择地点';

const LazyGoogleMapLocationPicker = React.forwardRef<
  GoogleMapLocationPickerHandle,
  LazyGoogleMapLocationPickerProps
>(
  (
    {
      buttonText,
      showAddressPreview = true,
      initialStructuredAddress,
      initialPlaceData,
      initialPlace,
      initialDetailAddress,
      disabled = false,
      ...restProps
    },
    ref,
  ) => {
    const intl = useIntl();
    const [isLoaded, setIsLoaded] = useState(false);
    const pickerRef = useRef<GoogleMapLocationPickerHandle>(null);

    useImperativeHandle(
      ref,
      () => ({
        ensureAddressPreview: () =>
          pickerRef.current?.ensureAddressPreview() ?? Promise.resolve(null),
      }),
      [],
    );

    const placeFirstLabel = intl.formatMessage({ id: placeFirstLabelId });

    // 构建地址预览文本（与 GoogleMapLocationPicker 中的 previewAddress 逻辑一致）
    const getAddressPreview = (): string => {
      // 优先级1：使用 initialPlace（PlaceResult 对象）
      if (initialPlace) {
        const parts: string[] = [];

        // vicinity（附近区域）
        if (initialPlace.vicinity) {
          parts.push(initialPlace.vicinity);
        }

        // name（地点名称）
        if (initialPlace.name) {
          parts.push(initialPlace.name);
        }

        // 详细地址
        if (initialDetailAddress) {
          parts.push(initialDetailAddress);
        }

        return parts.length > 0 ? parts.join(' · ') : placeFirstLabel;
      }

      // 优先级2：使用 initialStructuredAddress
      if (initialStructuredAddress) {
        const parts: string[] = [];

        // vicinity（附近区域）
        if (initialStructuredAddress.vicinity) {
          parts.push(initialStructuredAddress.vicinity);
        }

        // name（地点名称）
        if (initialStructuredAddress.name) {
          parts.push(initialStructuredAddress.name);
        }

        // 详细地址
        const detail = initialDetailAddress || initialStructuredAddress.detail;
        if (detail) {
          parts.push(detail);
        }

        return parts.length > 0 ? parts.join(' · ') : placeFirstLabel;
      }

      // 优先级3：使用 initialPlaceData
      if (initialPlaceData) {
        const parts: string[] = [];

        // vicinity（附近区域）
        if (initialPlaceData.vicinity) {
          parts.push(initialPlaceData.vicinity);
        }

        // name（地点名称）
        if (initialPlaceData.name) {
          parts.push(initialPlaceData.name);
        }

        // 详细地址
        const detail = initialDetailAddress || initialPlaceData.detail;
        if (detail) {
          parts.push(detail);
        }

        return parts.length > 0 ? parts.join(' · ') : placeFirstLabel;
      }

      return placeFirstLabel;
    };

    const addressPreview = getAddressPreview();

    // 默认按钮文案
    const defaultButtonText = disabled
      ? intl.formatMessage({ id: '组件.地址.在地图中查看地址' })
      : addressPreview && addressPreview !== placeFirstLabel
        ? intl.formatMessage({ id: '组件.地址.在地图中编辑地址' })
        : intl.formatMessage({ id: '组件.地址.在地图中选择地址' });

    const finalButtonText = buttonText || defaultButtonText;

    // 如果已经加载，直接显示地图组件
    if (isLoaded) {
      return (
        <GoogleMapLocationPicker
          ref={pickerRef}
          initialStructuredAddress={initialStructuredAddress}
          initialPlaceData={initialPlaceData}
          initialPlace={initialPlace}
          initialDetailAddress={initialDetailAddress}
          disabled={disabled}
          {...restProps}
        />
      );
    }

    // 未加载时，显示按钮
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Button
            type="primary"
            size="large"
            icon={disabled ? <EyeOutlined /> : <EnvironmentOutlined />}
            onClick={() => setIsLoaded(true)}
            style={{
              marginBottom:
                showAddressPreview &&
                addressPreview &&
                addressPreview !== placeFirstLabel
                  ? 16
                  : 0,
            }}
          >
            {finalButtonText}
          </Button>
          {showAddressPreview &&
            addressPreview &&
            addressPreview !== placeFirstLabel && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: 'rgba(0, 0, 0, 0.65)',
                  textAlign: 'left',
                  wordBreak: 'break-word',
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {intl.formatMessage({ id: '组件.地址.当前地址' })}
                </div>
                <div>{addressPreview}</div>
              </div>
            )}
        </div>
      </Card>
    );
  },
);

export default LazyGoogleMapLocationPicker;
